/** t331u B7 (moved from apps/web features/ops so the phone pages share it): form model for "Schedule a checklist" (kept pure so it can be unit-tested). Times are building wall-clock HH:mm. */
import type { ChecklistAssignee, ChecklistSchedule, ChecklistTargets, ChecklistTemplate } from './checklists';

export interface ScheduleForm { template_id: string; location_id: string; weekdays: number[]; window_start: string; window_end: string; assignee_type: ChecklistAssignee; assignee_id: string;
  /** t704u: who does it when assignee_type is 'targets' (people, departments / groups, positions, buildings). */
  targets: ChecklistTargets }
export const SCHEDULE_ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

/** "06:00:00" or "6:00" → "06:00"; anything unparseable is returned unchanged for the API to reject. */
export function scheduleHhmm(t: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(t ?? '');
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : t;
}

export function initialScheduleForm(edit: ChecklistSchedule | null, templates: Pick<ChecklistTemplate, 'id' | 'archived_at'>[], locations: { id: string }[]): ScheduleForm {
  if (edit) return { template_id: edit.template_id, location_id: edit.location_id, weekdays: [...edit.weekdays].sort((a, b) => a - b), window_start: scheduleHhmm(edit.window_start), window_end: scheduleHhmm(edit.window_end), assignee_type: edit.assignee_type, assignee_id: edit.assignee_id ?? '', targets: scheduleTargetsOf(edit) };
  return { template_id: templates.find(t => !t.archived_at)?.id ?? '', location_id: locations[0]?.id ?? '', weekdays: [...SCHEDULE_ALL_DAYS], window_start: '06:00', window_end: '08:00', assignee_type: 'building', assignee_id: '', targets: noTargets() };
}

export function toggleWeekday(days: number[], d: number): number[] {
  return days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => a - b);
}

/** Returns an error message, or null when the form can be saved. */
export function validateScheduleForm(f: ScheduleForm, isNew: boolean): string | null {
  if (isNew && !f.template_id) return 'Choose a checklist';
  if (isNew && !f.location_id) return 'Choose a building';
  if (!f.weekdays.length) return 'Pick at least one day';
  if (!/^\d{2}:\d{2}$/.test(f.window_start) || !/^\d{2}:\d{2}$/.test(f.window_end)) return 'Enter both times';
  if (f.window_start === f.window_end) return "'From' and 'Due by' can't be the same time";
  if ((f.assignee_type === 'user' || f.assignee_type === 'position') && !f.assignee_id) return f.assignee_type === 'user' ? 'Choose a person' : 'Choose a position';
  if (f.assignee_type === 'targets' && !targetCount(f.targets)) return 'Choose who does it';
  return null;
}

export const noTargets = (): ChecklistTargets => ({ people: [], teams: [], positions: [], buildings: [] });
export const targetCount = (t: ChecklistTargets | null | undefined) => (t ? t.people.length + t.teams.length + t.positions.length + t.buildings.length : 0);
/** t704u: a schedule's "who" as targets (one person / position as a one-item list), for the people picker. */
export function scheduleTargetsOf(s: Pick<ChecklistSchedule, 'assignee_type' | 'assignee_id' | 'assignee_targets'>): ChecklistTargets {
  if (s.assignee_type === 'targets' && s.assignee_targets) return { ...noTargets(), ...s.assignee_targets };
  if (s.assignee_type === 'user' && s.assignee_id) return { ...noTargets(), people: [s.assignee_id] };
  if (s.assignee_type === 'position' && s.assignee_id) return { ...noTargets(), positions: [s.assignee_id] };
  return noTargets();
}
/** t704u: what's stored for "who": one person or position stays 'user' / 'position' (older clients read it), more is 'targets'. */
export function scheduleWhoPayload(f: Pick<ScheduleForm, 'assignee_type' | 'assignee_id' | 'targets'>): { assignee_type: ChecklistAssignee; assignee_id: string | null; assignee_targets: ChecklistTargets | null } {
  if (f.assignee_type === 'targets') {
    const t = f.targets;
    if (targetCount(t) === 1 && t.people.length) return { assignee_type: 'user', assignee_id: t.people[0], assignee_targets: null };
    if (targetCount(t) === 1 && t.positions.length) return { assignee_type: 'position', assignee_id: t.positions[0], assignee_targets: null };
    return { assignee_type: 'targets', assignee_id: null, assignee_targets: { people: [...t.people], teams: [...t.teams], positions: [...t.positions], buildings: [...t.buildings] } };
  }
  return { assignee_type: f.assignee_type, assignee_id: f.assignee_type === 'user' || f.assignee_type === 'position' ? f.assignee_id || null : null, assignee_targets: null };
}

export function schedulePayload(f: ScheduleForm) {
  return { weekdays: f.weekdays, window_start: scheduleHhmm(f.window_start), window_end: scheduleHhmm(f.window_end), ...scheduleWhoPayload(f) };
}

export const SCHEDULE_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const SCHEDULE_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const fmtScheduleDays = (d: number[]) => d.length === 7 ? 'Every day' : d.join(',') === '1,2,3,4,5' ? 'Weekdays' : d.map(n => SCHEDULE_WEEKDAYS[n - 1]).join(', ');
/** Who does it, as the desktop table words it. */
export const scheduleWho = (s: Pick<ChecklistSchedule, 'assignee_type' | 'assignee_name'>) => s.assignee_name ?? (s.assignee_type === 'on_shift' ? 'Whoever is on shift' : s.assignee_type === 'targets' ? 'Chosen people' : 'Anyone at the building');
/** Phone row lines: "Bangor · Weekdays · 06:00–08:00" and who (plus "Paused" when inactive). */
export const scheduleLines = (s: ChecklistSchedule) => [`${s.location_name} · ${fmtScheduleDays(s.weekdays)} · ${scheduleHhmm(s.window_start)}–${scheduleHhmm(s.window_end)}`, [scheduleWho(s), s.active ? null : 'Paused'].filter(Boolean).join(' · ')];
export const SCHEDULE_WHO_OPTIONS: readonly { id: ChecklistAssignee; name: string }[] = [
  { id: 'building', name: 'Anyone working at the building' }, { id: 'on_shift', name: 'Whoever is on shift in the window' }, { id: 'position', name: 'A position' }, { id: 'user', name: 'A named person' },
];
/** t302u: a register runs at its own building and is never "anyone at the building" (staff would see each other's attendance). */
export function scheduleForTemplate(f: ScheduleForm, tpl: Pick<ChecklistTemplate, 'kind' | 'register_location_id'> | undefined): ScheduleForm {
  const reg = tpl?.kind === 'register' ? tpl.register_location_id : null;
  return reg ? { ...f, location_id: reg, assignee_type: f.assignee_type === 'building' ? 'on_shift' : f.assignee_type } : f;
}
export const SCHEDULES_NOTE = 'When each checklist is due, and where. Changes apply from the next checklist; ones already due stay.';

/** t533u: "Daily", "Weekdays", "Weekends" or "Mon, Wed" for the inline schedule lines on the Checklists page. */
export const scheduleDaysShort = (d: number[]) => {
  const k = [...d].sort((a, b) => a - b).join(',');
  return k === '1,2,3,4,5,6,7' ? 'Daily' : k === '1,2,3,4,5' ? 'Weekdays' : k === '6,7' ? 'Weekends' : [...d].sort((a, b) => a - b).map(n => SCHEDULE_WEEKDAYS[n - 1]).join(', ');
};

export interface ScheduleGroup {
  /** "Daily at 06:00–08:00 · Ashton, Bangor" (plus who does it unless it's anyone at the building, and "Paused"). */
  text: string;
  active: boolean;
  schedules: ChecklistSchedule[];
}

/**
 * t533u: a template's schedules as a few muted lines. Schedules are one building each, so the same days, times and
 * person at several buildings read as one line ("Daily at 08:00–10:00 · Ashton, Bangor"); edit and pause act on the line.
 */
export function scheduleGroups(list: ChecklistSchedule[]): ScheduleGroup[] {
  const groups = new Map<string, ScheduleGroup>();
  for (const s of list) {
    const key = [[...s.weekdays].sort((a, b) => a - b).join(','), scheduleHhmm(s.window_start), scheduleHhmm(s.window_end), s.assignee_type, s.assignee_id ?? '', s.active].join('|');
    const g = groups.get(key);
    if (g) g.schedules.push(s); else groups.set(key, { text: '', active: s.active, schedules: [s] });
  }
  return [...groups.values()].map(g => {
    const s = g.schedules[0];
    g.schedules.sort((a, b) => a.location_name.localeCompare(b.location_name, 'en-GB'));
    const where = g.schedules.map(x => x.location_name).join(', ');
    const who = s.assignee_type === 'building' ? null : scheduleWho(s);
    g.text = [`${scheduleDaysShort(s.weekdays)} at ${scheduleHhmm(s.window_start)}–${scheduleHhmm(s.window_end)}`, where, who, g.active ? null : 'Paused'].filter(Boolean).join(' · ');
    return g;
  }).sort((a, b) => Number(b.active) - Number(a.active) || a.schedules[0].window_start.localeCompare(b.schedules[0].window_start));
}

/**
 * t705u: where a schedule runs, as the desktop editor. A new schedule for a checklist used at several buildings can
 * cover several of them, or All buildings (still one schedule at each); an edit or a register stays at one building.
 */
export function scheduleBuildings(o: { isNew: boolean; register: boolean; buildingIds: readonly string[]; all: boolean; picked: readonly string[]; locationId: string }): { many: boolean; where: string[] } {
  const many = o.isNew && !o.register && o.buildingIds.length > 1;
  if (!many) return { many, where: o.locationId ? [o.locationId] : [] };
  return { many, where: o.all ? [...o.buildingIds] : o.picked.filter(id => o.buildingIds.includes(id)) };
}

/** The form's problem once the buildings are known ("Choose at least one building" when several could be picked). */
export function scheduleProblem(f: ScheduleForm, isNew: boolean, b: { many: boolean; where: string[] }): string | null {
  if (b.many && !b.where.length) return 'Choose at least one building';
  return validateScheduleForm(b.many ? { ...f, location_id: b.where[0] } : f, isNew);
}

export const scheduleSavedText = (buildings: number) => (buildings > 1 ? `Scheduled at ${buildings} buildings` : 'Schedule saved');
